import { type ProfileDto } from "@crowdcoursing/api/openapi";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { valueOrFallback } from "~utils/sanitizeDto";
import { makeProfileAvatarUrl } from "~utils/url";

import { deleteProfilePicture, savePersonalInfo, saveProfilePicture } from "../actions/profile";
import { type User } from "./state";

// https://github.com/CrowdCoursing/CrowdCoursingApi/blob/develop/CrowdCoursing.Common/Constants/RoleConstants.cs

/**
 * @description RoleConstants
 *
 * {@link https://github.com/CrowdCoursing/CrowdCoursingApi/blob/develop/CrowdCoursing.Common/Constants/RoleConstants.cs 🔗 RoleConstants}
 */
export type RoleConstants =
  | "Admin"
  | "Candidate"
  | "Tutor"
  | "Volunteer"
  | "Educator"
  | "Front Office"
  | "Principal"
  | "Student"
  | "Volunteer Coordinator"
  | "Expert"
  | "Human Resources"
  | "Member"
  | "Facilitator"
  | "Parent"
  | "Sales";

function getRole(role: RoleConstants) {
  return role;
}

/** @deprecated We are phasing out `redux` */
function makeUser(profile: ProfileDto): User {
  const sanitized = valueOrFallback(
    profile.userDto,
    {
      basePhoneNumber: "",
      countryCode: "",
      email: "",
      firstName: "",
      id: "",
      isDailyNotificationsOn: false,
      isDeleted: false,
      isWeeklyNotificationsOn: false,
      lastName: "",
      phoneNumber: "",
      roles: [],
      timestamp: "",
    },
    "",
  );

  return {
    avatarUrl: makeProfileAvatarUrl(sanitized.id || "MISSING_AVATAR_URL"),
    email: sanitized.email || "",
    firstName: sanitized.firstName || "",
    id: sanitized.id || "",
    isAdministrator: Boolean(sanitized.roles?.includes(getRole("Admin"))),
    isDailyNotificationsOn: sanitized.isDailyNotificationsOn ?? false,
    isEducator: Boolean(sanitized.roles?.includes(getRole("Educator"))),
    isProfileReady: profile.isProfileReady ?? false,
    isWeeklyNotificationsOn: sanitized.isWeeklyNotificationsOn ?? false,
    lastName: sanitized.lastName || "",
    phoneNumber: sanitized.phoneNumber || "",
  };
}

/** @deprecated We are phasing out `redux` */
const initialState = null as User | null;

/** @deprecated We are phasing out `redux` */
const forceNewProfilePicture = (user: User | null) => {
  if (!user) {
    return;
  }
  // set a arbitrary query string to trigger re-rendering of the new
  // profile picture
  user.avatarUrl = `${makeProfileAvatarUrl(user.id)}?t=${new Date().getTime()}`;
};

const slice = createSlice({
  // respond to actions defined elsewhere
  extraReducers: (builder) =>
    builder
      .addCase(savePersonalInfo.fulfilled, (state, action) => {
        if (!state) {
          return state;
        }
        return { ...state, ...action.payload };
      })
      .addCase(savePersonalInfo.rejected, (state) => state)
      .addCase(saveProfilePicture.fulfilled, forceNewProfilePicture)
      .addCase(saveProfilePicture.rejected, (state) => state)
      .addCase(deleteProfilePicture.fulfilled, forceNewProfilePicture)
      .addCase(deleteProfilePicture.rejected, (state) => state),
  initialState,
  name: "user",
  reducers: {
    logout() {
      return null;
    },
    whoamiSuccess(_, action: PayloadAction<{ user: ProfileDto }>) {
      return makeUser(action.payload.user);
    },
  },
});

/** @deprecated We are phasing out `redux` */
export const actions = slice.actions;

/** @deprecated We are phasing out `redux` */
export const userReducer = slice.reducer;
